import {
  Users,
  Rocket,
  Laptop,
  Megaphone,
  Headphones,
  ChevronRight,
  Mail,
} from "lucide-react";
import { Navigation } from "@/components/Navigation";
import { Footer } from "@/components/Footer";

export default function Careers() {
  const applicationFormUrl = "https://tally.so/r/b55701";

  const teams = [
    {
      id: "engineering",
      icon: <Laptop className="h-5 w-5 text-primary" />,
      title: "Engineering",
      description:
        "Help us build the AI learning experience for students across Saudi Arabia and the Arab world.",
    },
    {
      id: "marketing",
      icon: <Megaphone className="h-5 w-5 text-primary" />,
      title: "Marketing & Growth",
      description:
        "Grow the Shattara community through authentic storytelling and student-focused campaigns.",
    },
    {
      id: "support",
      icon: <Headphones className="h-5 w-5 text-primary" />,
      title: "Customer Support / Student Success",
      description:
        "Support learners with empathy and cultural understanding in Arabic and English.",
    },
  ];

  const upcomingRoles = [
    {
      role: "Full-Stack Software Engineer (React + Supabase)",
      type: "Full-time",
      location: "Remote / Saudi Arabia",
    },
    {
      role: "AI/ML Engineer (LLMs, RAG, Arabic Embeddings)",
      type: "Full-time",
      location: "Remote / Saudi Arabia",
    },
    {
      role: "Growth & Community Manager (Students & Universities)",
      type: "Full-time",
      location: "Remote / Saudi Arabia",
    },
    {
      role: "Customer Support Specialist (Arabic/English)",
      type: "Full/Part-time",
      location: "Remote / Saudi Arabia",
    },
  ];

  const openApplication = () => {
    window.open(applicationFormUrl, "_blank", "noopener,noreferrer");
  };

  return (
    <div className="min-h-screen flex flex-col">
      <Navigation />
      
      <main className="flex-grow">
        <div className="mx-auto max-w-6xl px-4 py-14 space-y-20">
          {/* Hero */}
          <section className="space-y-6 text-center">
            <div className="inline-flex items-center gap-2 rounded-full bg-primary/10 px-4 py-1 text-primary font-medium text-xs">
              <Users className="h-3.5 w-3.5" /> Careers
            </div>

            <h1 className="text-3xl md:text-4xl font-semibold tracking-tight">
              Help us shape the future of learning in Saudi Arabia and the Arab world
            </h1>

            <p className="max-w-2xl mx-auto text-muted-foreground text-sm md:text-base">
              We're building an AI-powered study companion created for students in our region — 
              in our language, in our culture, and for our future. If that mission excites you, 
              we'd love to meet you.
            </p>

            <button
              onClick={openApplication}
              className="inline-flex items-center gap-2 rounded-lg bg-primary px-5 py-2.5 text-primary-foreground text-sm font-medium shadow hover:bg-primary/90 transition"
            >
              Join Our Talent Network
              <ChevronRight className="h-4 w-4" />
            </button>
          </section>

          {/* Our mission */}
          <section className="grid gap-8 md:grid-cols-2 items-center">
            <div className="space-y-4">
              <h2 className="text-xl font-semibold flex items-center gap-2">
                <Rocket className="h-5 w-5 text-primary" /> Why Shattara AI exists
              </h2>
              <p className="text-sm text-muted-foreground leading-relaxed">
                Shattara AI is here to help students study smarter, understand faster, 
                and learn with confidence. We believe that generative AI should empower 
                students, not replace their effort — and should respect our Arabic language 
                and cultural identity.
              </p>
            </div>

            <div className="rounded-2xl border bg-card p-6 text-sm space-y-3">
              <h3 className="font-medium text-foreground mb-1">Our Values</h3>
              <ul className="space-y-2 text-muted-foreground">
                <li>🤝 Students First — every decision starts with improving a learner's life.</li>
                <li>🌱 Growth Mindset — we experiment, learn, and improve together.</li>
                <li>🕌 Respect & Integrity — our work reflects our culture and purpose.</li>
                <li>🚀 Ownership — freedom with responsibility, not micromanagement.</li>
                <li>❤️ Kindness & Trust — we treat each other with empathy and care.</li>
              </ul>
            </div>
          </section>

          {/* Teams */}
          <section className="space-y-8">
            <h2 className="text-xl font-semibold">Teams We Are Growing</h2>
            <div className="grid gap-6 md:grid-cols-3">
              {teams.map((team) => (
                <div
                  key={team.id}
                  className="border rounded-xl p-6 bg-card space-y-3"
                >
                  <div className="flex items-center gap-2">
                    {team.icon}
                    <h3 className="font-medium">{team.title}</h3>
                  </div>
                  <p className="text-xs text-muted-foreground leading-relaxed">
                    {team.description}
                  </p>
                </div>
              ))}
            </div>
          </section>

          {/* Roles */}
          <section className="space-y-8">
            <h2 className="text-xl font-semibold">Upcoming Roles (Hiring Soon)</h2>
            <div className="space-y-4">
              {upcomingRoles.map((r, index) => (
                <div
                  key={index}
                  className="border rounded-xl p-4 bg-card text-sm flex flex-col sm:flex-row sm:items-center sm:justify-between"
                >
                  <span className="font-medium">{r.role}</span>
                  <div className="text-xs text-muted-foreground flex gap-4 mt-1 sm:mt-0">
                    <span>{r.type}</span>
                    <span>{r.location}</span>
                  </div>
                </div>
              ))}
            </div>

            <button
              onClick={openApplication}
              className="inline-flex items-center gap-2 rounded-lg bg-primary px-5 py-2.5 text-primary-foreground text-sm font-medium shadow hover:bg-primary/90 transition"
            >
              Apply Early
              <ChevronRight className="h-4 w-4" />
            </button>
          </section>

          {/* General Application */}
          <section className="border rounded-2xl bg-card p-8 space-y-4 text-center">
            <h2 className="text-xl font-semibold">Didn't find your role?</h2>
            <p className="max-w-xl mx-auto text-sm text-muted-foreground">
              We review every application with care. Share your work, your experience, 
              and the kind of impact you want to make — and we'll reach out if there's a strong match.
            </p>

            <button
              onClick={openApplication}
              className="inline-flex items-center gap-2 rounded-lg bg-primary px-5 py-2.5 text-primary-foreground text-sm font-medium shadow hover:bg-primary/90 transition"
            >
              Join the Talent Network
              <Mail className="h-4 w-4" />
            </button>
          </section>
        </div>
      </main>

      <Footer />
    </div>
  );
}
